import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Api } from "../lib";
import reliLogo from "../assets/img/reliexchange_logo.png";

const EMAIL_TYPE_LABELS: Record<string, string> = {
  expiration_reminder: "policy expiration reminder",
};

function getEmailTypeLabel(emailType: string): string {
  return EMAIL_TYPE_LABELS[emailType] || emailType.replace(/_/g, " ");
}

export default function UnsubscribePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [emailType, setEmailType] = useState("");
  const [resubscribing, setResubscribing] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  const processUnsubscribe = useCallback(async () => {
    if (!token) {
      setError("Invalid unsubscribe link.");
      setLoading(false);
      return;
    }
    try {
      const { status, data } = await Api.get(
        `/unsubscribe?token=${encodeURIComponent(token)}`,
      );
      if (status === 200 && data?.success) {
        setUnsubscribed(true);
        setEmailType(data.email_type || "");
      } else {
        setError("Unable to process your request. The link may be invalid.");
      }
    } catch {
      setError("Unable to process your request. The link may be invalid.");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    processUnsubscribe();
  }, [processUnsubscribe]);

  const handleResubscribe = async () => {
    if (!token) return;
    setResubscribing(true);
    try {
      const { status, data } = await Api.post("/resubscribe", { token });
      if (status === 200 && data?.success) {
        setUnsubscribed(false);
      }
    } catch {
      setError("Unable to resubscribe. Please try again later.");
    }
    setResubscribing(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f5f5",
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <img src={reliLogo} alt="RELI Exchange" style={{ height: 40 }} />
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 5,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Oops
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {error}
              </Typography>
            </>
          ) : unsubscribed ? (
            <>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                You've been unsubscribed
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                You will no longer receive{" "}
                <strong>{getEmailTypeLabel(emailType)}</strong> emails.
              </Typography>
              <Button
                variant="outlined"
                onClick={handleResubscribe}
                disabled={resubscribing}
              >
                {resubscribing ? "Resubscribing..." : "Resubscribe"}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                You're resubscribed
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You will continue to receive{" "}
                <strong>{getEmailTypeLabel(emailType)}</strong> emails.
              </Typography>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
